import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { ShieldCheck, Percent, Chrome, Wallet, ArrowRight, ShoppingCart, ChevronDown, HelpCircle, Download, Sparkles, Lock, CreditCard, TrendingUp, Shield } from 'lucide-react';
import FallbackImage from '../components/FallbackImage';
import { Button, Card } from '../components/ui';
import { db } from '../firebase';
import { dedupeDealsByBrand } from '../utils/dedupeDeals';

// ─── Scroll reveal hook ───
function useScrollReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    el.classList.add('visible');
                    observer.unobserve(el);
                }
            },
            { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return ref;
}

// ─── Animated counter ───
function AnimatedCounter({ end, duration = 2000, suffix = '', prefix = '' }) {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const startTime = performance.now();
                    const animate = (now) => {
                        const progress = Math.min((now - startTime) / duration, 1);
                        const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
                        setCount(Math.floor(eased * end));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [end, duration]);

    return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

// ─── Section wrapper with reveal ───
function Section({ children, className = '', id }) {
    const ref = useScrollReveal();
    return (
        <div ref={ref} className={`reveal py-24 px-4 ${className}`} id={id}>
            {children}
        </div>
    );
}

// ─── Featured Deals Ticker ───
const FeaturedDealsTicker = ({ navigate }) => {
    const [deals, setDeals] = useState([]);

    useEffect(() => {
        const fetchTopDeals = async () => {
            try {
                const dealsRef = collection(db, 'dealData');
                const q = query(dealsRef, orderBy("discount", "desc"), limit(20));
                const querySnapshot = await getDocs(q);
                const topDeals = querySnapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(deal => typeof deal.discount === 'number' && deal.discount > 0 && deal.retailerSlug);
                setDeals(dedupeDealsByBrand(topDeals));
            } catch (error) {
                console.error("Error fetching top deals:", error);
            }
        };
        fetchTopDeals();
    }, []);

    if (deals.length === 0) return null;

    const tickerItems = [...deals, ...deals];

    const handleDealClick = (e, slug) => {
        e.preventDefault();
        navigate(`/retailer/${slug}`);
    };

    return (
        <div className="bg-gradient-to-r from-brand-800 via-brand-700 to-brand-800 text-white py-3 overflow-hidden relative shadow-inner">
            <div className="absolute top-0 left-0 bottom-0 w-24 bg-gradient-to-r from-brand-800 to-transparent z-10"></div>
            <div className="absolute top-0 right-0 bottom-0 w-24 bg-gradient-to-l from-brand-800 to-transparent z-10"></div>
            <div className="ticker-wrap">
                <div className="ticker-move">
                    {tickerItems.map((deal, index) => (
                        <a
                            key={`${deal.id}-${index}`}
                            href={`/retailer/${deal.retailerSlug}`}
                            onClick={(e) => handleDealClick(e, deal.retailerSlug)}
                            className="ticker-item group rounded-lg"
                        >
                            <FallbackImage
                                srcs={deal.retailerLogoUrls}
                                alt={`${deal.retailerName} logo`}
                                className="h-8 max-w-24 object-contain mr-3 bg-white p-1 rounded-md"
                            />
                            <span className="font-semibold group-hover:underline">{deal.retailerName}</span>
                            <span className="ml-2 font-bold text-brand-300">{deal.discount.toFixed(2)}% Off</span>
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── Feature Card ───
const FeatureCard = ({ icon, title, stat, children }) => (
    <Card hover className="relative overflow-hidden group">
        <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-brand-100 to-brand-200 dark:from-brand-900/50 dark:to-brand-800/30 text-brand-700 dark:text-brand-400 mb-5 group-hover:scale-110 transition-transform duration-300">
            {icon}
        </div>
        <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">{title}</h3>
        {stat && <p className="text-xs font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-wider mb-2">{stat}</p>}
        <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-sm">{children}</p>
    </Card>
);

// ─── How It Works Step ───
const HowItWorksStep = ({ icon, title, children, stepNumber }) => (
    <div className="relative text-center group">
        <div className="relative inline-flex items-center justify-center h-20 w-20 mx-auto rounded-2xl bg-white dark:bg-slate-800 shadow-card mb-5 border border-slate-200 dark:border-slate-700 group-hover:shadow-card-hover group-hover:scale-105 transition-all duration-300">
            {icon}
            <span className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center shadow-md">
                {stepNumber}
            </span>
        </div>
        <h4 className="text-lg font-bold text-slate-800 dark:text-white mb-2">{title}</h4>
        <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{children}</p>
    </div>
);

// ─── FAQ Item ───
const FAQItem = ({ question, answer, isOpen, onToggle }) => (
    <div className="border-b border-slate-200 dark:border-slate-700 last:border-b-0">
        <button
            onClick={onToggle}
            className="w-full py-5 flex items-center justify-between text-left hover:text-brand-700 dark:hover:text-brand-400 transition-colors group"
        >
            <span className="text-base font-semibold text-slate-800 dark:text-white pr-4 group-hover:text-brand-700 dark:group-hover:text-brand-400">{question}</span>
            <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center transition-all duration-200 ${isOpen ? 'bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400 rotate-180' : 'bg-slate-100 dark:bg-slate-700 text-slate-400'}`}>
                <ChevronDown size={18} />
            </div>
        </button>
        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-sm">{answer}</p>
        </div>
    </div>
);

const faqData = [
    {
        question: "Is Gift Scout really free?",
        answer: "Yes! Gift Scout is completely free to use. We believe everyone should have access to smart savings tools. There are no hidden fees, premium tiers, or subscriptions."
    },
    {
        question: "How secure is my gift card data?",
        answer: "Your data is protected with end-to-end encryption using a PIN that only you know. Your gift card codes are encrypted before they ever leave your device, and even we cannot access them. It's the same level of security used by banks."
    },
    {
        question: "Which browsers does the extension support?",
        answer: "The Gift Scout extension is currently available for Google Chrome and all Chromium-based browsers (like Microsoft Edge, Brave, and Opera). Firefox support is planned for the future."
    },
    {
        question: "What happens if I forget my PIN?",
        answer: "We have a secure PIN recovery process that requires you to re-authenticate with your account. This ensures that only you can recover access to your encrypted data."
    },
    {
        question: "Can I access my cards on multiple devices?",
        answer: "Absolutely! Your encrypted gift card data syncs across all your devices. Just sign in with the same account and enter your PIN to unlock your cards anywhere."
    }
];


export default function HomePage({ navigate }) {
    const [openFAQ, setOpenFAQ] = useState(null);

    const handleLinkClick = (e, path) => {
        e.preventDefault();
        navigate(path);
    };

    const toggleFAQ = (index) => {
        setOpenFAQ(openFAQ === index ? null : index);
    };

    return (
        <div className="bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
            {/* ═══ Hero Section ═══ */}
            <div className="relative overflow-hidden">
                {/* Animated gradient mesh */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-brand-200/30 dark:bg-brand-500/10 rounded-full blur-[100px] animate-blob-1"></div>
                    <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-emerald-200/20 dark:bg-emerald-500/5 rounded-full blur-[120px] animate-blob-2"></div>
                    <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-accent-200/10 dark:bg-accent-500/5 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2"></div>
                </div>

                {/* Subtle dot pattern */}
                <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle, #166534 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

                <div className="relative text-center py-24 md:py-32 px-4">
                    <div className="animate-bounce-slow">
                        <img src="/images/logo.webp" alt="Gift Scout Mascot" className="h-48 md:h-56 mx-auto mb-8 drop-shadow-2xl" width="224" height="224" />
                    </div>
                    <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight">
                        <span className="text-gradient-brand">Your Trusty Companion</span>
                        <br />
                        <span className="text-slate-800 dark:text-white">for Savings.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl mx-auto text-lg md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed">
                        Never lose track of a gift card again. Gift Scout is your all-in-one tool for smart, secure savings.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button as="a" href="/tool" onClick={(e) => handleLinkClick(e, '/tool')} pill size="xl" iconRight={ArrowRight} className="shadow-lg hover:shadow-xl hover:scale-105 transition-all">
                            Start Managing Cards
                        </Button>
                        <Button as="a" variant="outline" href="/extension" onClick={(e) => handleLinkClick(e, '/extension')} pill size="xl" icon={Chrome} className="shadow-md hover:scale-105 transition-all">
                            Get the Extension
                        </Button>
                    </div>

                    {/* Social proof */}
                    <div className="mt-10 flex items-center justify-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                        <div className="flex -space-x-2">
                            <div className="h-8 w-8 rounded-full bg-brand-200 dark:bg-brand-800 border-2 border-white dark:border-slate-900 flex items-center justify-center text-brand-700 dark:text-brand-300 text-xs font-bold">GS</div>
                            <div className="h-8 w-8 rounded-full bg-emerald-200 dark:bg-emerald-800 border-2 border-white dark:border-slate-900 flex items-center justify-center text-emerald-700 dark:text-emerald-300 text-xs font-bold">🔒</div>
                            <div className="h-8 w-8 rounded-full bg-accent-200 dark:bg-accent-800 border-2 border-white dark:border-slate-900 flex items-center justify-center text-accent-700 dark:text-accent-300 text-xs font-bold">💰</div>
                        </div>
                        <span>Join smart shoppers saving more every day</span>
                    </div>
                </div>
            </div>

            {/* Live deals ticker */}
            <FeaturedDealsTicker navigate={navigate} />

            {/* ═══ Stats Banner ═══ */}
            <Section>
                <div className="container mx-auto max-w-5xl">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { icon: CreditCard, value: 1000, suffix: '+', label: 'Gift Cards Managed' },
                            { icon: TrendingUp, value: 500, prefix: '$', suffix: 'K+', label: 'Savings Tracked' },
                            { icon: Shield, value: 256, suffix: '-bit', label: 'AES Encryption' },
                            { icon: Percent, value: 100, suffix: '%', label: 'Free to Use' },
                        ].map((stat, i) => (
                            <Card key={i} className="text-center p-6 group hover:shadow-card-hover transition-all duration-300">
                                <stat.icon className="h-8 w-8 mx-auto mb-3 text-brand-600 dark:text-brand-400 group-hover:scale-110 transition-transform" />
                                <p className="text-3xl font-extrabold text-slate-800 dark:text-white tabular-nums">
                                    <AnimatedCounter end={stat.value} prefix={stat.prefix || ''} suffix={stat.suffix || ''} />
                                </p>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mt-1">{stat.label}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </Section>

            {/* ═══ Features Section ═══ */}
            <Section className="bg-white dark:bg-slate-800/50">
                <div className="container mx-auto max-w-6xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white">Everything a Smart Shopper Needs</h2>
                        <p className="mt-4 text-lg text-slate-500 dark:text-slate-400">From your wallet to your browser, we've got you covered.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <FeatureCard icon={<ShieldCheck size={28} />} title="Encrypted Card Vault" stat="Bank-grade security">
                            Your personal digital wallet for gift cards. Add, track, and update balances with end-to-end encryption. Your data is yours alone.
                        </FeatureCard>
                        <FeatureCard icon={<Percent size={28} />} title="Live Deal Scouting" stat="1,000+ stores tracked">
                            Why pay full price? We scan the web's top marketplaces to find you the best discounts on gift cards for your favorite stores.
                        </FeatureCard>
                        <FeatureCard icon={<Chrome size={28} />} title="The Checkout Companion" stat="One-click autofill">
                            Our handy browser extension gives you instant access to your gift cards at checkout and can autofill your details with a single click.
                        </FeatureCard>
                    </div>
                </div>
            </Section>

            {/* ═══ Extension Promo ═══ */}
            <Section>
                <div className="container mx-auto max-w-6xl">
                    <div className="bg-gradient-to-br from-brand-700 via-brand-800 to-emerald-900 rounded-3xl shadow-card-prominent text-white relative overflow-hidden gradient-border">
                        <div className="absolute inset-0 opacity-10 pointer-events-none">
                            <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
                            <div className="absolute bottom-10 right-10 w-96 h-96 bg-brand-400 rounded-full blur-3xl"></div>
                        </div>
                        <div className="relative z-10 p-10 md:p-16">
                            <div className="flex flex-col md:flex-row items-center gap-12">
                                <div className="flex-1 text-center md:text-left">
                                    <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-brand-200 text-sm font-medium mb-6">
                                        <Sparkles size={16} className="mr-2" /> Free Chrome Extension
                                    </div>
                                    <h2 className="text-3xl md:text-5xl font-bold mb-6">
                                        Supercharge Your Checkout
                                    </h2>
                                    <p className="text-lg text-brand-100 mb-8 leading-relaxed">
                                        The Gift Scout extension lives in your browser, ready to auto-fill your gift card codes whenever you're shopping.
                                    </p>
                                    <div className="flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
                                        <Button
                                            as="a"
                                            href="https://chrome.google.com/webstore"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="secondary"
                                            pill
                                            size="xl"
                                            icon={Download}
                                            className="bg-white text-brand-800 hover:bg-brand-50 shadow-lg hover:shadow-xl hover:scale-105 transition-all border-none"
                                        >
                                            Add to Chrome — It's Free
                                        </Button>
                                    </div>
                                    <div className="mt-6 flex items-center justify-center md:justify-start gap-6 text-brand-200 text-sm">
                                        <span className="flex items-center"><Lock size={14} className="mr-1.5" /> Secure</span>
                                        <span className="flex items-center"><ShieldCheck size={14} className="mr-1.5" /> Privacy-First</span>
                                    </div>
                                </div>
                                <div className="flex-shrink-0">
                                    <img
                                        src="/images/logo_magnifying_glass.webp"
                                        alt="Gift Scout with Magnifying Glass"
                                        className="h-56 md:h-72 drop-shadow-2xl"
                                        loading="lazy"
                                        decoding="async"
                                        width="288"
                                        height="288"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* ═══ How It Works ═══ */}
            <Section className="bg-white dark:bg-slate-800/50">
                <div className="container mx-auto max-w-5xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white">Get Started in Three Easy Steps</h2>
                        <p className="mt-4 text-lg text-slate-500 dark:text-slate-400">From signup to savings in minutes.</p>
                    </div>
                    <div className="relative">
                        {/* Connecting dashed line (desktop only) */}
                        <div className="hidden md:block absolute top-10 left-[16%] right-[16%] h-0.5 border-t-2 border-dashed border-brand-200 dark:border-brand-800"></div>
                        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-12">
                            <HowItWorksStep icon={<Wallet size={36} className="text-brand-700 dark:text-brand-400" />} title="Add Your Cards" stepNumber="1">
                                Securely add your existing gift cards to the manager. We'll keep track of the balances for you.
                            </HowItWorksStep>
                            <HowItWorksStep icon={<Download size={36} className="text-brand-700 dark:text-brand-400" />} title="Install the Extension" stepNumber="2">
                                Add the free Chrome extension to unlock auto-fill magic at any online checkout.
                            </HowItWorksStep>
                            <HowItWorksStep icon={<ShoppingCart size={36} className="text-brand-700 dark:text-brand-400" />} title="Save at Checkout" stepNumber="3">
                                Shop normally—Gift Scout will pop up when you need it, filling in your codes instantly.
                            </HowItWorksStep>
                        </div>
                    </div>
                </div>
            </Section>

            {/* ═══ Trust & Security ═══ */}
            <Section>
                <div className="container mx-auto max-w-4xl">
                    <Card className="p-8 md:p-12 border-brand-100 dark:border-brand-900/30">
                        <div className="flex flex-col md:flex-row items-center gap-8">
                            <div className="flex-shrink-0">
                                <div className="h-24 w-24 rounded-3xl bg-gradient-to-br from-brand-100 to-brand-200 dark:from-brand-900/50 dark:to-brand-800/30 flex items-center justify-center">
                                    <Shield size={48} className="text-brand-700 dark:text-brand-400" />
                                </div>
                            </div>
                            <div>
                                <h2 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white mb-3">Your Security is Our Priority</h2>
                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                    Gift Scout uses <strong className="text-brand-700 dark:text-brand-400">256-bit AES encryption</strong> — the same standard used by banks and governments. Your gift card codes are encrypted on your device before they ever leave it, using a PIN that only you know. Even we can't access your data — and that's by design.
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>
            </Section>

            {/* ═══ FAQ Section ═══ */}
            <Section className="bg-white dark:bg-slate-800/50">
                <div className="container mx-auto max-w-3xl">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-100 to-brand-200 dark:from-brand-900/50 dark:to-brand-800/30 text-brand-700 dark:text-brand-400 mb-4">
                            <HelpCircle size={28} />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white">Frequently Asked Questions</h2>
                        <p className="mt-4 text-lg text-slate-500 dark:text-slate-400">Got questions? We've got answers.</p>
                    </div>
                    <Card padded={false} className="divide-y divide-slate-200 dark:divide-slate-700 px-6 md:px-8">
                        {faqData.map((faq, index) => (
                            <FAQItem
                                key={index}
                                question={faq.question}
                                answer={faq.answer}
                                isOpen={openFAQ === index}
                                onToggle={() => toggleFAQ(index)}
                            />
                        ))}
                    </Card>
                </div>
            </Section>

            {/* ═══ Final CTA ═══ */}
            <Section>
                <div className="container mx-auto max-w-4xl text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-4">
                        Ready to Stop Losing Money on Unused Gift Cards?
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto">
                        Free, private, and built for people who hate paying full price.
                    </p>
                    <Button as="a" href="/tool" onClick={(e) => handleLinkClick(e, '/tool')} pill size="xl" iconRight={ArrowRight} className="shadow-lg hover:shadow-xl hover:scale-105 transition-all">
                        Get Started Free
                    </Button>
                </div>
            </Section>
        </div>
    );
}
